/** @jsxImportSource @emotion/react */
'use memo'

// web/src/features/event/components/EventDetailsColumn.tsx
import css from '@emotion/css/macro'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { ROUTES } from '@sportninja/common/constants/app'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type { SnEvent } from '@sportninja/common/features/event/types'
import { formatArrivalTime } from '@sportninja/common/features/event/formatArrivalTime'
import { formatEventDateRangeParts } from '@sportninja/common/features/event/formatEventDateRange'
import { formatRecurrence } from '@sportninja/common/features/event/formatRecurrence'
import { getEventCategory } from '@sportninja/common/features/event/eventCategories'
import { resolveEventCompetition } from '@sportninja/common/features/event/eventOwnership'
import { getBrowserTimezone } from '@sportninja/common/ts-utils/timezone-display'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import {
  font,
  paragraphSmall,
  weightBold,
  weightSemiBold,
} from 'src/components/css'
import { useEventRowTimes } from '../hooks/useEventRowTimes'
import { formatTimeRange } from '@sportninja/common/features/event/formatTimeRange'
import {
  DetailRow,
  cardStyle,
  secondaryTextStyle,
  sectionLabelStyle,
  valueStyle,
} from './eventCardStyles'

const TEAM_PLACEHOLDER = '/images/generic_placeholders/placeholder_team.png'

interface EventDetailsColumnProps {
  event: SnEvent
}

/**
 * Everything about the event EXCEPT where it happens — date/time, arrival,
 * repeat rule, and the parent team. Location lives in its own card
 * (`EventLocationCard`) so the page can weight the two columns differently.
 */
const EventDetailsColumn: React.FC<EventDetailsColumnProps> = ({ event }) => {
  // Parts, not the flat string: a multi-day event returns two lines so each
  // date gets its own row instead of one string wrapping mid-range.
  const dateRange = formatEventDateRangeParts(event.starts_at, event.ends_at)
  // Second line in the event's own zone, matching the schedule row — so the
  // same event reads the same way in the list and on its page.
  const { eventTime } = useEventRowTimes({
    startsAt: event.starts_at,
    timezone: event.timezone,
  })
  // The END instant's own second line. Called unconditionally (hooks cannot be
  // conditional); `ends_at` may be null, which the hook already tolerates by
  // returning ''.
  const { eventTime: endEventTime } = useEventRowTimes({
    startsAt: event.ends_at ?? '',
    timezone: event.timezone,
  })
  // The MULTI-DAY layout keeps the two apart, one under each labelled endpoint.
  // The same-day layout has a single main line reading as a range, so its
  // second line has to be one too — showing only the start there invites
  // reading it as the whole event.
  //
  // `formatTimeRange` is the same helper mobile's detail hero uses, so both
  // platforms collapse the shared zone label identically — and keep BOTH when
  // the range crosses a clock change.
  const sameDayEventTime = formatTimeRange(eventTime, endEventTime)
  const arrival = formatArrivalTime(event.arrival_time_minutes)
  const category = getEventCategory(event.category)
  // Null for a one-off; individual fields may be '' until the API returns the
  // full rule (only `recurrence_type` comes back today).
  //
  // The viewer's zone is passed for the same reason mobile passes its device
  // zone: naming it is what puts the permanent-DST correction on the cut-off
  // date, and it earns the abbreviation that matches every other time on this
  // screen. See ts-utils/luxon-permanent-dst.
  const recurrence = formatRecurrence(event, getBrowserTimezone())

  const teamRoute = event.team ? `${ROUTES.TEAM_ROOT}/${event.team.id}` : null
  // The list response omits `team` entirely; some responses use `name_full`.
  const rawTeamName = event.team?.name ?? event.team?.name_full
  const teamName = rawTeamName ? capitalizeWords(rawTeamName) : undefined
  const teamLogo = resolveAssetUrl(event.team?.image)
  // Null for a team-owned event, and for a competition event whose payload
  // carries no usable id/name pair — the row then shows only the badge.
  const competition = resolveEventCompetition(event)

  return (
    <section>
      <h2 css={sectionLabelStyle}>{capitalizeWords('Details')}</h2>
      <div css={cardStyle}>
        {/* Rendered as the same coloured badge the schedule row shows, so one
            event reads identically in both places. */}
        {category && (
          <DetailRow iconName='tag' label='Event Type'>
            <span
              css={categoryTagStyles}
              style={{
                backgroundColor: category.color,
                color: category.textColor,
              }}
            >
              {category.label}
            </span>
          </DetailRow>
        )}

        <DetailRow iconName='calendar-alt' label='Date & Time'>
          <span css={valueStyle}>
            {dateRange.lines.length === 0 ? (
              'Not set'
            ) : dateRange.endpoints ? (
              /*
                A multi-day event is shown as two LABELLED blocks. Read as one
                run ("… 5:12 PM – Wed, Aug 26 … 5:12 PM") the two ends are easy
                to confuse, and the single trailing zone line appeared to
                qualify only the second date. Each end now carries its own
                local time and its own event-zone line directly beneath it.
              */
              <>
                <span css={endpointBlockStyle}>
                  <span css={endpointLabelStyle}>Start</span>
                  <span css={dateLineStyle}>
                    {dateRange.endpoints.start}
                    {dateRange.zone ? (
                      <span css={zoneInlineStyle}>{dateRange.zone}</span>
                    ) : null}
                  </span>
                  {eventTime && (
                    <span css={secondaryTextStyle}>{eventTime}</span>
                  )}
                </span>
                <span css={endpointBlockStyle}>
                  <span css={endpointLabelStyle}>End</span>
                  <span css={dateLineStyle}>
                    {dateRange.endpoints.end}
                    {dateRange.zone ? (
                      <span css={zoneInlineStyle}>{dateRange.zone}</span>
                    ) : null}
                  </span>
                  {endEventTime && (
                    <span css={secondaryTextStyle}>{endEventTime}</span>
                  )}
                </span>
              </>
            ) : (
              /* Same-day or open-ended: one line, exactly as before. */
              <>
                <span css={dateLineStyle}>
                  {dateRange.lines[0]}
                  {dateRange.zone ? (
                    <span css={zoneInlineStyle}>{dateRange.zone}</span>
                  ) : null}
                </span>
                {sameDayEventTime && (
                  <span css={secondaryTextStyle}>{sameDayEventTime}</span>
                )}
              </>
            )}
          </span>
        </DetailRow>

        {arrival && (
          <DetailRow iconName='clock' label='Arrival Time'>
            <span css={valueStyle}>{arrival}</span>
          </DetailRow>
        )}

        {teamRoute && teamName && (
          <DetailRow iconName='user-friends' label='Team'>
            <Link to={teamRoute} css={teamLinkStyle}>
              <img
                src={teamLogo ?? TEAM_PLACEHOLDER}
                alt=''
                css={teamLogoStyle}
                onError={(e) => {
                  e.currentTarget.src = TEAM_PLACEHOLDER
                }}
              />
              <span>{teamName}</span>
            </Link>
          </DetailRow>
        )}

        {/* After Team, closing the ownership block: names the competition the
            event sits in and links to it.

            COMPETITION EVENTS ONLY — a team event has none to name, so the row
            is dropped rather than left labelled and empty. The type badge that
            used to sit here is gone too: on a row that only ever renders for a
            competition event it said what the row's own presence already
            does. */}
        {competition && (
          <DetailRow iconName='sitemap' label='Belongs To'>
            <span css={ownerValueStyle}>
              <Link
                to={`${ROUTES.SCHEDULE_ROOT}/${competition.id}`}
                css={competitionLinkStyle}
              >
                {competition.name}
              </Link>
            </span>
          </DetailRow>
        )}

        {/* Repeat rule, as its own labelled block rather than another icon row
            — it describes a series, not a single field, and each line below is
            hidden individually while the API still omits the rule fields. */}
        {recurrence && (
          <div css={repeatSectionStyle}>
            <h3 css={repeatHeadingStyle}>{capitalizeWords('Repeating')}</h3>

            {/* Two columns: Repeats + Days Of Week, then Repeat Until beneath.
                The rule's start is the event's own Date & Time above, so there
                is no separate "starts on" row. Each cell stays conditional —
                a monthly rule has no days, and the cut-off may be absent. */}
            <div css={repeatGridStyle}>
              <DetailRow iconName='redo' label='Repeats'>
                <span css={valueStyle}>{recurrence.type}</span>
              </DetailRow>

              {recurrence.days && (
                <DetailRow iconName='calendar-week' label='Days Of Week'>
                  <span css={valueStyle}>{recurrence.days}</span>
                </DetailRow>
              )}
            </div>

            {recurrence.repeatUntil && (
              <DetailRow iconName='stop' label='Repeat Until'>
                <span css={valueStyle}>{recurrence.repeatUntil}</span>
              </DetailRow>
            )}
          </div>
        )}
      </div>
    </section>
  )
}

export default EventDetailsColumn

// ---- styled ----

/* Divider above the repeat block so it reads as a distinct group inside the
   same card, rather than four more rows continuing the list. */
const repeatSectionStyle = css`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-top: 20px;
  border-top: 1px solid ${colors.SOFT_STEEL};
`

/* Two equal columns, collapsing to one when the card gets narrow — the details
   card can sit at 340px in the right-hand slot, where two columns would leave
   each value wrapping mid-date. `minmax(0, 1fr)` rather than `1fr` so a long
   value (a full weekday list) shrinks instead of forcing the grid wider. */
const repeatGridStyle = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 1400px) {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1100px) {
    /* Single-column page layout gives the card full width again. */
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`

const repeatHeadingStyle = css`
  ${font.body}
  ${paragraphSmall}
  ${weightBold}
  color: ${colors.PRIMARY};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`

/* Solid fill in the category's own colour — identical treatment to the badge on
   the schedule row (see EventListRow's categoryTagStyles), so the same event
   looks the same in both places — including the type metrics it shares with the
   game row's badges. Inline-flex + self-start so the pill hugs its text rather
   than stretching across the row. Text colour is set inline from
   `category.textColor`. */
const categoryTagStyles = css`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  padding: 3px 10px;
  border-radius: 4px;
  ${font.body}
  ${weightSemiBold}
  font-size: 12px;
  line-height: 15px;
  letter-spacing: 1.44px;
  text-transform: uppercase;
  white-space: nowrap;
`

/* Badge and competition name on one line, wrapping to a second when a long
   competition name will not fit beside the badge. */
const ownerValueStyle = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

/* Same treatment as the team link above, so the two ownership links read as a
   pair rather than two different kinds of thing. */
const competitionLinkStyle = css`
  ${font.body}
  ${paragraphSmall}
  color: ${colors.PRIMARY};
  overflow-wrap: anywhere;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }
`

const teamLinkStyle = css`
  ${font.body}
  ${paragraphSmall}
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: ${colors.PRIMARY};
  overflow-wrap: anywhere;

  &:hover {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }
`

const teamLogoStyle = css`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 4px;
  object-fit: contain;
`

/* Each date line is its own row. `valueStyle` is a plain inline span, so the
   multi-day lines need an explicit block to stack rather than wrap together. */
const dateLineStyle = css`
  display: block;
`

/* The zone abbreviation, riding on the END of the date line rather than
   sitting under it, and set in the SAME font and colour as the date so the
   two read as one value ("Tue, Aug 25, 2026 3:18 PM PDT") rather than a date
   with a footnote attached.

   Inline only — it inherits type and colour from `valueStyle` above it, so
   nothing is restated here. `margin-left` rather than a literal space: JSX
   collapses whitespace between an expression and an element, so a plain ' '
   would not survive. */
const zoneInlineStyle = css`
  margin-left: 4px;
`

/* One end of a multi-day range: its label, date, zone and second clock as a
   single group. The gap separates the two ends from each other — without it
   the End label sits as close to the Start block as to its own date. */
const endpointBlockStyle = css`
  display: block;

  & + & {
    margin-top: 12px;
  }
`

/* The START / END marker. Small-caps and muted, matching the section labels
   above it — it names the value beneath rather than being read as part of it. */
const endpointLabelStyle = css`
  display: block;
  ${font.body}
  ${weightSemiBold}
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
  margin-bottom: 2px;
`
